import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { NonRefundableNotice, FinalBookingConfirmation } from './terms/NonRefundableNotice';
import { PassportValidityNotice } from './booking/PassportValidityNotice';
import { PassengerCard, PassengerFields, ContactFields } from './booking/PassengerFields';
import { FlightPaymentPlan, useDepositQuote } from './booking/FlightPaymentPlan';
import { BfmFlightCard } from './flight/BfmFlightCard';
import { travellerError, passportAcknowledgement, sixMonthsAfter } from '../utils/travellerValidation';
import { buildTermsAcceptance } from './terms/nonRefundableTerms';
import { 
  Plane, 
  Clock, 
  User, 
  Users,
  Calendar,
  Mail,
  MapPin,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  PlaneTakeoff,
  PlaneLanding,
  AlertCircle,
  Copy,
  Loader2,
  Printer,
  Download,
  FileText,
  Armchair,
  Briefcase,
  Check,
  CreditCard
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import { Money } from '../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingFlow = ({ 
  flight: rawFlight, 
  searchParams,
  onBack,
  onComplete,
  skipItineraryReview = false 
}) => {
  // Start at step 2 if skipItineraryReview is true (e.g., after auth)
  const [step, setStep] = useState(skipItineraryReview ? 2 : 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [finalAccepted, setFinalAccepted] = useState(false);
  const [noticeAccepted, setNoticeAccepted] = useState(false);
  const [noticeAcceptedAt, setNoticeAcceptedAt] = useState(null);
  const [passportOk, setPassportOk] = useState(false);
  const [pnrCopied, setPnrCopied] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState('FULL');

  const handlePayNow = async () => {
    if (!bookingResult?.pnr) return;
    setIsPaying(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/flights/payment/create`, {
        pnr: bookingResult.pnr,
        origin_url: window.location.origin
      });
      if (!data.checkout_url) throw new Error('No checkout URL returned');
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Unable to start payment. Please try again.');
      setIsPaying(false);
    }
  };
  
  const itineraryRef = useRef(null);
  const confirmationRef = useRef(null);
  const pnrSectionRef = useRef(null);
  const contactFormRef = useRef(null);
  
  // Auto-scroll to contact form when entering step 2 (Passenger Details)
  // This triggers when step changes to 2 (from button click or initial mount)
  useEffect(() => {
    if (step === 2) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        // First scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Then scroll to contact form after a brief delay
        setTimeout(() => {
          if (contactFormRef.current) {
            contactFormRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
            // Focus on the first input field in contact form
            const firstInput = contactFormRef.current.querySelector('input, select');
            if (firstInput) {
              setTimeout(() => firstInput.focus(), 300);
            }
          }
        }, 400);
      }, 100);
    }
  }, [step]);
  
  // Auto-scroll to PNR section when booking is confirmed (step 3)
  useEffect(() => {
    if (step === 3 && bookingResult?.pnr && pnrSectionRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        pnrSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        // Add a subtle highlight animation
        pnrSectionRef.current.classList.add('animate-pulse');
        setTimeout(() => {
          pnrSectionRef.current?.classList.remove('animate-pulse');
        }, 2000);
      }, 300);
    }
  }, [step, bookingResult]);
  
  const [passengers, setPassengers] = useState([]);
  const [contactTitle, setContactTitle] = useState('');
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactAddress, setContactAddress] = useState('');
  const [contactCity, setContactCity] = useState('');
  const [contactCountry, setContactCountry] = useState('');
  const [contactPostalCode, setContactPostalCode] = useState('');

  // Normalize flight data - handle both Combined view and Mix & Match view structures
  const flight = React.useMemo(() => {
    // Mix & Match structure: { outbound: {...}, return: {...}, combinedPrice: number }
    if (rawFlight.outbound && rawFlight.return) {
      const outbound = rawFlight.outbound;
      const returnFlight = rawFlight.return;
      return {
        id: `${outbound.id || 'out'}-${returnFlight.id || 'ret'}`,
        // Outbound flight details
        airline: outbound.airline,
        airline_code: outbound.airline_code,
        from: outbound.from,
        to: outbound.to,
        departure_time: outbound.departure_time,
        arrival_time: outbound.arrival_time,
        duration: outbound.duration,
        is_direct: outbound.is_direct,
        stops: outbound.stops,
        // Return flight details
        return_airline: returnFlight.airline || outbound.airline,
        return_airline_code: returnFlight.return_airline_code || returnFlight.airline_code || outbound.airline_code,
        return_departure_time: returnFlight.return_departure_time,
        return_arrival_time: returnFlight.return_arrival_time,
        return_duration: returnFlight.return_duration,
        return_is_direct: returnFlight.return_is_direct,
        return_stops: returnFlight.return_stops,
        // Price
        price: rawFlight.combinedPrice,
        combinedPrice: rawFlight.combinedPrice
      };
    }
    // Combined view structure - already in correct format
    return rawFlight;
  }, [rawFlight]);
  const passportBlocked = (passengers || []).some((p) => p.passport_expiry && flight?.departure_date && p.passport_expiry < sixMonthsAfter(flight.departure_date));
  const isMultiCity = flight?.trip_type === 'multi-city' && Array.isArray(flight?.legs) && flight.legs.length > 1;

  const passengerCounts = {
    adults: searchParams?.adults || 1,
    youth: searchParams?.youth || 0,
    children: searchParams?.children || 0,
    infants: searchParams?.infants || 0
  };

  const totalPassengers = passengerCounts.adults + passengerCounts.youth + passengerCounts.children + passengerCounts.infants;

  React.useEffect(() => {
    const initialPassengers = [];
    
    for (let i = 0; i < passengerCounts.adults; i++) {
      initialPassengers.push({
        type: 'ADULT',
        title: '',
        first_name: '',
        last_name: '',
        date_of_birth: '',
        passport_number: '',
        passport_expiry: ''
      });
    }
    for (let i = 0; i < passengerCounts.youth; i++) {
      initialPassengers.push({
        type: 'YOUTH',
        title: '',
        first_name: '',
        last_name: '',
        date_of_birth: '',
        passport_number: '',
        passport_expiry: ''
      });
    }
    for (let i = 0; i < passengerCounts.children; i++) {
      initialPassengers.push({
        type: 'CHILD',
        title: '',
        first_name: '',
        last_name: '',
        date_of_birth: '',
        passport_number: '',
        passport_expiry: ''
      });
    }
    for (let i = 0; i < passengerCounts.infants; i++) {
      initialPassengers.push({
        type: 'INFANT',
        title: '',
        first_name: '',
        last_name: '',
        date_of_birth: '',
        passport_number: '',
        passport_expiry: ''
      });
    }
    
    setPassengers(initialPassengers);
  }, [passengerCounts.adults, passengerCounts.youth, passengerCounts.children, passengerCounts.infants]);

  const calculatePriceBreakdown = () => {
    // Handle Mix & Match flights (which have outbound/return/combinedPrice structure)
    // vs Combined flights (which have direct price property)
    const basePrice = flight.combinedPrice || flight.price || 0;
    
    if (!basePrice || basePrice === 0) {
      return {
        adult: { count: passengerCounts.adults, unitPrice: 0, total: 0 },
        youth: { count: passengerCounts.youth, unitPrice: 0, total: 0 },
        child: { count: passengerCounts.children, unitPrice: 0, total: 0 },
        infant: { count: passengerCounts.infants, unitPrice: 0, total: 0 },
        grandTotal: 0
      };
    }
    
    const adultPrice = basePrice / totalPassengers;
    const youthPrice = adultPrice * 0.9;
    const childPrice = adultPrice * 0.75;
    const infantPrice = adultPrice * 0.1;
    
    return {
      adult: { count: passengerCounts.adults, unitPrice: adultPrice, total: adultPrice * passengerCounts.adults },
      youth: { count: passengerCounts.youth, unitPrice: youthPrice, total: youthPrice * passengerCounts.youth },
      child: { count: passengerCounts.children, unitPrice: childPrice, total: childPrice * passengerCounts.children },
      infant: { count: passengerCounts.infants, unitPrice: infantPrice, total: infantPrice * passengerCounts.infants },
      grandTotal: basePrice
    };
  };

  const priceBreakdown = calculatePriceBreakdown();
  const depositQuote = useDepositQuote({ passengers: totalPassengers, source: flight.source || 'mock', departureDate: flight.departure_date, total: priceBreakdown.grandTotal, isNdc: !!flight.is_ndc });
  const payingDeposit = paymentPlan === 'DEPOSIT' && depositQuote?.available;
  const amountDueNow = payingDeposit ? depositQuote.deposit : priceBreakdown.grandTotal;
  useEffect(() => { if (depositQuote && !depositQuote.available) setPaymentPlan('FULL'); }, [depositQuote]);

  const formatTime = (dateTime) => {
    if (!dateTime) return '';
    
    // If already in HH:MM format, return as-is
    if (typeof dateTime === 'string' && dateTime.match(/^\d{2}:\d{2}$/)) {
      return dateTime;
    }
    
    // Handle formats like "13:55:00Z" or "04:45:00+04:00" - extract HH:MM
    if (typeof dateTime === 'string') {
      const timeMatch = dateTime.match(/^(\d{2}:\d{2})/);
      if (timeMatch) {
        return timeMatch[1];
      }
      
      // Also handle ISO datetime format like "2024-01-15T13:55:00Z"
      const isoMatch = dateTime.match(/T(\d{2}:\d{2})/);
      if (isoMatch) {
        return isoMatch[1];
      }
    }
    
    // Try to parse as a full datetime string
    try {
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return dateTime; // Return original if invalid
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateTime; // Return original on error
    }
  };

  const formatDate = (dateTime, fallbackDate = null) => {
    if (!dateTime && !fallbackDate) return '';
    
    // If dateTime is just a time string like "07:15", use the fallbackDate
    if (dateTime && typeof dateTime === 'string' && dateTime.match(/^\d{2}:\d{2}$/)) {
      if (fallbackDate) {
        try {
          return new Date(fallbackDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
        } catch {
          return fallbackDate;
        }
      }
      return '';
    }
    
    try {
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return fallbackDate || '';
      return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return fallbackDate || '';
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return '';
    return duration.replace('PT', '').replace('H', 'h ').replace('M', 'm');
  };

  const updatePassengerField = useCallback((index, field, value) => {
    setPassengers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const validatePassengers = () => {
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      if (!p.title || !p.first_name || !p.last_name || !p.date_of_birth || !p.passport_number || !p.passport_expiry) {
        setError(`Please fill in all required fields for Passenger ${i + 1}`);
        return false;
      }
      if (!/^[A-Z0-9]{5,15}$/i.test(p.passport_number.trim())) {
        setError(`Passenger ${i + 1}: passport number should be 5–15 letters/numbers`);
        return false;
      }
      const te = travellerError(p, `Passenger ${i + 1}`, flight.departure_date, flight.return_departure_date || flight.departure_date);
      if (te) { setError(te); return false; }
    }
    if (passportBlocked) { setError('A passenger\u2019s passport has less than six months\u2019 validity on the date of travel \u2014 it must be renewed before booking'); return false; }
    if (!passportOk) { setError('Please confirm you have checked every traveller\u2019s passport validity'); return false; }
    if (!contactEmail || !contactPhone) {
      setError('Please provide email and phone number for lead passenger');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmitBooking = async () => {
    if (!validatePassengers()) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Format passengers for backend API
      const formattedPassengers = passengers.map(p => ({
        type: p.type || 'ADULT',
        title: p.title,
        first_name: p.first_name,
        last_name: p.last_name,
        date_of_birth: p.date_of_birth,
        gender: ['Mrs', 'Ms', 'Miss'].includes(p.title) ? 'Female' : 'Male',
        nationality: p.nationality || 'UK',
        passport_number: (p.passport_number || '').trim().toUpperCase() || null,
        passport_expiry: p.passport_expiry || null,
        email: contactEmail,
        phone: contactPhone
      }));

      // Pay first: the PNR is only issued once Revolut confirms the card payment
      const response = await axios.post(`${API_URL}/api/flights/payment/start`, {
        origin_url: window.location.origin,
        terms_acceptance: { ...buildTermsAcceptance({ refundable: flight.fare_details?.refundable ?? null, fare_details: flight.fare_details || null, fare_attributes: flight.fare_attributes || null }, noticeAcceptedAt), ...passportAcknowledgement(passportOk) },
        flight_id: flight.id || `flight_${Date.now()}`,
        flight_data: {
          from: flight.from,
          to: flight.to,
          origin: flight.from || flight.origin,
          destination: flight.to || flight.destination,
          // CRITICAL: Include departure and return DATES for PNR creation
          departure_date: flight.departure_date,
          departure_time: flight.departure_time,
          departure_datetime: flight.departure_datetime,
          arrival_date: flight.arrival_date,
          arrival_time: flight.arrival_time,
          arrival_datetime: flight.arrival_datetime,
          duration: flight.duration,
          airline: flight.airline,
          airline_code: flight.airline_code || flight.airline,
          flight_number: flight.flight_number,
          is_direct: flight.is_direct,
          stops: flight.stops,
          price: flight.price,
          cabin_class: flight.cabin_class || 'economy',
          booking_class: flight.booking_class,
          // Return flight details - CRITICAL: Include return DATES
          return_departure_date: flight.return_departure_date,
          return_departure_time: flight.return_departure_time,
          return_departure_datetime: flight.return_departure_datetime,
          return_arrival_date: flight.return_arrival_date,
          return_arrival_time: flight.return_arrival_time,
          return_arrival_datetime: flight.return_arrival_datetime,
          return_duration: flight.return_duration,
          return_airline_code: flight.return_airline_code,
          return_flight_number: flight.return_flight_number,
          return_booking_class: flight.return_booking_class,
          return_is_direct: flight.return_is_direct,
          return_stops: flight.return_stops,
          // Source - important for Sabre PNR creation
          source: flight.source || 'mock',
          // NDC offer details for modern workflow
          offer_id: flight.offer_id || flight.id,
          offer_items: flight.offer_items || [{ id: "1" }],
          // Full segment details for real Sabre flights
          segments: flight.segments || null,
          return_segments: flight.return_segments || null,
          // Fare details for NDC
          fare_details: flight.fare_details || null,
          // Multi-city: the complete BFM itinerary (all sectors) so revalidation + PNR cover every leg
          trip_type: flight.trip_type || null,
          legs: flight.legs || null,
          key: flight.key || null,
          leg_airlines: flight.leg_airlines || null,
          same_airline: flight.same_airline ?? null,
          validating_carrier: flight.validating_carrier || null,
          route_summary: flight.route_summary || null,
          final_destination: flight.final_destination || null,
          return_date: flight.return_date || null,
          is_ndc: flight.is_ndc ?? null
        },
        passengers: formattedPassengers,
        contact: {
          title: passengers[0]?.title || '',
          first_name: passengers[0]?.first_name || '',
          last_name: passengers[0]?.last_name || '',
          email: contactEmail,
          phone: contactPhone
        },
        passenger_counts: passengerCounts,
        total_price: priceBreakdown.grandTotal,
        currency: 'GBP',
        payment_plan: payingDeposit ? 'DEPOSIT' : 'FULL'
      });
      
      if (!response.data?.checkout_url) throw new Error('No checkout URL returned by the payment gateway');
      sessionStorage.setItem('pendingFlightPayment', JSON.stringify({ ref: response.data.ref, amount: response.data.amount, plan: response.data.payment_plan, at: Date.now() }));
      window.location.href = response.data.checkout_url; // → Revolut hosted checkout → /flight-payment-success?bref=PFxxxxxx
      return;
    } catch (err) {
      console.error('Booking error:', err);
      // Check for detail (HTTPException) or message field
      const rawDetail = err.response?.data?.detail;
      let errorMessage = (rawDetail && typeof rawDetail === 'object' ? [rawDetail.message, ...(rawDetail.errors || [])].filter(Boolean).join(' \u2022 ') : rawDetail) || err.response?.data?.message || 'Failed to create booking. Please try again.';
      
      // Add helpful guidance for inventory errors
      if (errorMessage.includes('sold out') || errorMessage.includes('no longer available') || errorMessage.includes('seat class')) {
        errorMessage = `${errorMessage}\n\nTip: Flight availability changes in real-time. Please go back and select a different flight option.`;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) {
      toast.error('No PNR to copy');
      return;
    }
    
    try {
      // Try modern clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for HTTP or older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
        } catch (execErr) {
          console.warn('execCommand failed:', execErr);
        }
        
        document.body.removeChild(textArea);
      }
      
      // Show success feedback with toast
      setPnrCopied(true);
      toast.success('PNR copied to clipboard!', {
        description: text,
        duration: 3000,
      });
      
      // Reset copied state after 3 seconds
      setTimeout(() => setPnrCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Show manual copy option
      toast.info(`Copy this PNR manually: ${text}`, {
        duration: 10000
      });
      // Still mark as copied since user can see the PNR
      setPnrCopied(true);
      setTimeout(() => setPnrCopied(false), 3000);
    }
  };

  const handlePrintItinerary = () => {
    const printWindow = window.open('', '_blank');
    
    // Helper to format time to clean 24hr format
    const cleanTime = (time) => {
      if (!time) return '';
      const match = time.match(/^(\d{2}):(\d{2})/);
      return match ? `${match[1]}:${match[2]}` : time;
    };
    
    // Generate passenger list HTML
    const passengersHtml = passengers.map((p, idx) => `
      <tr class="passenger-row">
        <td class="passenger-num">${idx + 1}</td>
        <td class="passenger-name">${(p.title || 'MR').toUpperCase()} ${(p.first_name || '').toUpperCase()} ${(p.last_name || '').toUpperCase()}</td>
        <td class="passenger-type">${p.type === 'ADULT' ? 'ADULT' : p.type === 'CHILD' ? 'CHILD' : p.type === 'INFANT' ? 'INFANT' : 'ADULT'}</td>
        <td class="passenger-ticket">E-TICKET</td>
      </tr>
    `).join('');
    
    // Generate layover info
    const getLayoverHtml = (layovers) => {
      if (!layovers || layovers.length === 0) return '';
      return `<div class="layover-box">
        <span class="layover-icon">⏱</span>
        Connection: ${layovers.map(l => `${l.airport} (${l.duration || 'N/A'})`).join(' → ')}
      </div>`;
    };
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>E-Ticket Itinerary - Flight380</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; background: #f5f5f5; padding: 20px; color: #333; }
          .ticket-container { max-width: 800px; margin: 0 auto; background: white; box-shadow: 0 2px 20px rgba(0,0,0,0.1); }
          
          /* Header */
          .header { background: linear-gradient(135deg, #E73121 0%, #c92a1c 100%); color: white; padding: 25px 30px; display: flex; justify-content: space-between; align-items: center; }
          .logo { font-size: 28px; font-weight: bold; }
          .logo-icon { margin-right: 8px; }
          .header-right { text-align: right; }
          .header-right .doc-type { font-size: 20px; font-weight: 600; }
          .header-right .doc-date { font-size: 12px; opacity: 0.9; margin-top: 4px; }
          
          /* PNR Section */
          .pnr-section { background: #1a1a2e; color: white; padding: 20px 30px; display: flex; justify-content: space-between; align-items: center; }
          .pnr-left .label { font-size: 11px; color: #aaa; text-transform: uppercase; letter-spacing: 1px; }
          .pnr-left .value { font-size: 36px; font-weight: bold; letter-spacing: 4px; color: #00d4aa; margin-top: 5px; }
          .pnr-right { text-align: right; }
          .pnr-right .status { background: #00d4aa; color: #1a1a2e; padding: 6px 16px; border-radius: 20px; font-weight: 600; font-size: 12px; }
          .pnr-right .source { font-size: 11px; color: #aaa; margin-top: 8px; }
          
          /* Passenger Section */
          .section { padding: 25px 30px; border-bottom: 1px solid #eee; }
          .section-title { font-size: 14px; font-weight: 600; color: #E73121; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 1px; display: flex; align-items: center; gap: 8px; }
          .section-title::before { content: ''; width: 4px; height: 20px; background: #E73121; border-radius: 2px; }
          
          .passenger-table { width: 100%; border-collapse: collapse; }
          .passenger-table th { text-align: left; padding: 10px; background: #f8f9fa; font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
          .passenger-table td { padding: 12px 10px; border-bottom: 1px solid #eee; }
          .passenger-num { width: 40px; font-weight: 600; color: #E73121; }
          .passenger-name { font-weight: 600; font-size: 15px; }
          .passenger-type { color: #666; font-size: 13px; }
          .passenger-ticket { color: #00a651; font-weight: 600; font-size: 12px; }
          
          /* Flight Section */
          .flight-section { background: #fafbfc; }
          .flight-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px dashed #ddd; }
          .flight-route { }
          .flight-route .cities { font-size: 28px; font-weight: bold; color: #1a1a2e; }
          .flight-route .arrow { color: #E73121; margin: 0 15px; }
          .flight-route .airports { font-size: 13px; color: #666; margin-top: 5px; }
          .flight-type { text-align: right; }
          .flight-type .label { font-size: 11px; color: #666; text-transform: uppercase; }
          .flight-type .badge { background: ${flight.stops === 0 ? '#00a651' : '#ff9800'}; color: white; padding: 4px 12px; border-radius: 4px; font-size: 12px; font-weight: 600; margin-top: 5px; display: inline-block; }
          
          .flight-details { display: grid; grid-template-columns: 1fr auto 1fr; gap: 20px; align-items: center; }
          .flight-time { }
          .flight-time .time { font-size: 32px; font-weight: bold; color: #1a1a2e; }
          .flight-time .date { font-size: 13px; color: #666; margin-top: 2px; }
          .flight-time .airport-code { font-size: 18px; font-weight: 600; color: #E73121; margin-top: 8px; }
          .flight-time.arrival { text-align: right; }
          
          .flight-journey { text-align: center; position: relative; padding: 0 20px; }
          .flight-journey .duration { font-size: 13px; color: #666; margin-bottom: 10px; }
          .flight-journey .line { height: 2px; background: linear-gradient(to right, #E73121, #00d4aa); position: relative; }
          .flight-journey .line::before, .flight-journey .line::after { content: ''; position: absolute; top: 50%; width: 10px; height: 10px; border-radius: 50%; transform: translateY(-50%); }
          .flight-journey .line::before { left: 0; background: #E73121; }
          .flight-journey .line::after { right: 0; background: #00d4aa; }
          .flight-journey .plane { font-size: 20px; margin-top: 8px; }
          .flight-journey .airline { font-size: 12px; color: #666; margin-top: 5px; }
          .flight-journey .flight-num { font-weight: 600; color: #1a1a2e; }
          
          .layover-box { background: #fff3cd; border: 1px solid #ffc107; padding: 10px 15px; border-radius: 6px; margin-top: 15px; font-size: 13px; color: #856404; display: flex; align-items: center; gap: 8px; }
          .layover-icon { font-size: 16px; }
          
          /* Price Section */
          .price-section { background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); color: white; padding: 25px 30px; display: flex; justify-content: space-between; align-items: center; }
          .price-left .label { font-size: 12px; color: #aaa; text-transform: uppercase; }
          .price-left .amount { font-size: 36px; font-weight: bold; color: #00d4aa; }
          .price-left .includes { font-size: 11px; color: #888; margin-top: 5px; }
          .price-right { text-align: right; font-size: 13px; color: #aaa; }
          .price-right .passengers { margin-bottom: 5px; }
          
          /* Contact Section */
          .contact-section { background: #e8f5e9; }
          .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
          .contact-item .label { font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 4px; }
          .contact-item .value { font-size: 14px; font-weight: 500; }
          
          /* Footer */
          .footer { padding: 20px 30px; background: #f8f9fa; text-align: center; font-size: 11px; color: #666; }
          .footer .company { font-weight: 600; color: #E73121; margin-bottom: 5px; }
          .footer .notice { margin-top: 10px; padding: 10px; background: #fff3cd; border-radius: 4px; color: #856404; }
          
          @media print { 
            body { padding: 0; background: white; }
            .ticket-container { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="ticket-container">
          <!-- Header -->
          <div class="header">
            <div class="logo"><span class="logo-icon">✈</span>Flight380</div>
            <div class="header-right">
              <div class="doc-type">E-TICKET ITINERARY</div>
              <div class="doc-date">Issued: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}</div>
            </div>
          </div>
          
          <!-- PNR Section -->
          <div class="pnr-section">
            <div class="pnr-left">
              <div class="label">Booking Reference</div>
              <div class="value">${bookingResult?.pnr || 'PENDING'}</div>
            </div>
            <div class="pnr-right">
              <div class="status">✓ CONFIRMED</div>
              <div class="source">${bookingResult?.pnr_source === 'sabre' ? 'SABRE GDS' : 'Reference Number'}</div>
            </div>
          </div>
          
          <!-- Passengers -->
          <div class="section">
            <div class="section-title">Passenger Details</div>
            <table class="passenger-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Passenger Name</th>
                  <th>Type</th>
                  <th>Ticket</th>
                </tr>
              </thead>
              <tbody>
                ${passengersHtml}
              </tbody>
            </table>
          </div>
          
          <!-- Outbound Flight -->
          <div class="section flight-section">
            <div class="section-title">Outbound Flight</div>
            <div class="flight-header">
              <div class="flight-route">
                <div class="cities">${flight.from} <span class="arrow">→</span> ${flight.to}</div>
                <div class="airports">${flight.departure_airport || flight.from} to ${flight.arrival_airport || flight.to}</div>
              </div>
              <div class="flight-type">
                <div class="label">Flight Type</div>
                <div class="badge">${flight.stops === 0 ? 'DIRECT' : flight.stops + ' STOP'}</div>
              </div>
            </div>
            <div class="flight-details">
              <div class="flight-time">
                <div class="time">${cleanTime(flight.departure_time)}</div>
                <div class="date">${formatDate(flight.departure_date || flight.departure_datetime, flight.departure_date)}</div>
                <div class="airport-code">${flight.from}</div>
              </div>
              <div class="flight-journey">
                <div class="duration">${formatDuration(flight.duration)}</div>
                <div class="line"></div>
                <div class="plane">✈</div>
                <div class="airline">${flight.airline || flight.airline_name || 'Airline'}</div>
                <div class="flight-num">${flight.airline_code || ''} ${flight.flight_number || ''}</div>
              </div>
              <div class="flight-time arrival">
                <div class="time">${cleanTime(flight.arrival_time)}</div>
                <div class="date">${formatDate(flight.departure_date || flight.departure_datetime, flight.departure_date)}</div>
                <div class="airport-code">${flight.to}</div>
              </div>
            </div>
            ${getLayoverHtml(flight.layovers)}
          </div>
          
          ${flight.return_departure_time ? `
          <!-- Return Flight -->
          <div class="section flight-section">
            <div class="section-title">Return Flight</div>
            <div class="flight-header">
              <div class="flight-route">
                <div class="cities">${flight.to} <span class="arrow">→</span> ${flight.from}</div>
                <div class="airports">${flight.to} to ${flight.from}</div>
              </div>
              <div class="flight-type">
                <div class="label">Flight Type</div>
                <div class="badge">${(flight.return_stops || 0) === 0 ? 'DIRECT' : (flight.return_stops || 0) + ' STOP'}</div>
              </div>
            </div>
            <div class="flight-details">
              <div class="flight-time">
                <div class="time">${cleanTime(flight.return_departure_time)}</div>
                <div class="date">${formatDate(flight.return_departure_date || flight.return_departure_datetime, flight.return_departure_date)}</div>
                <div class="airport-code">${flight.to}</div>
              </div>
              <div class="flight-journey">
                <div class="duration">${formatDuration(flight.return_duration)}</div>
                <div class="line"></div>
                <div class="plane">✈</div>
                <div class="airline">${flight.airline || flight.airline_name || 'Airline'}</div>
                <div class="flight-num">${flight.return_airline_code || flight.airline_code || ''} ${flight.return_flight_number || ''}</div>
              </div>
              <div class="flight-time arrival">
                <div class="time">${cleanTime(flight.return_arrival_time)}</div>
                <div class="date">${formatDate(flight.return_departure_date || flight.return_departure_datetime, flight.return_departure_date)}</div>
                <div class="airport-code">${flight.from}</div>
              </div>
            </div>
            ${getLayoverHtml(flight.return_layovers)}
          </div>
          ` : ''}
          
          <!-- Contact -->
          <div class="section contact-section">
            <div class="section-title">Contact Information</div>
            <div class="contact-grid">
              <div class="contact-item">
                <div class="label">Email</div>
                <div class="value">${contactEmail || 'Not provided'}</div>
              </div>
              <div class="contact-item">
                <div class="label">Phone</div>
                <div class="value">${contactPhone || 'Not provided'}</div>
              </div>
            </div>
          </div>
          
          <!-- Price -->
          <div class="price-section">
            <div class="price-left">
              <div class="label">Total Amount Due</div>
              <div class="amount">£${priceBreakdown.grandTotal.toFixed(2)}</div>
              <div class="includes">All taxes & fees included</div>
            </div>
            <div class="price-right">
              <div class="passengers">${totalPassengers} Passenger(s)</div>
              <div>Payment: PENDING</div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="footer">
            <div class="company">Flight380 - Your Travel Partner</div>
            <div>www.flight380.co.uk | support@flight380.co.uk</div>
            <div class="notice">
              <strong>Important:</strong> Please arrive at the airport at least 3 hours before international departures. 
              Carry this itinerary and valid photo ID for check-in.
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const doc = new jsPDF();
      
      // Helper to format time
      const cleanTime = (time) => {
        if (!time) return 'N/A';
        const match = time?.toString().match(/^(\d{2}):(\d{2})/);
        return match ? `${match[1]}:${match[2]}` : time?.toString().substring(0, 5) || 'N/A';
      };
      
      // Safe text function to handle undefined values
      const safeText = (val) => val?.toString() || '';
      
      // Safe number formatting
      const safePrice = (val) => {
        const num = parseFloat(val);
        return isNaN(num) ? '0.00' : num.toFixed(2);
      };
      
      // Header
      doc.setFillColor(231, 49, 33);
      doc.rect(0, 0, 210, 35, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.text('Flight380', 20, 18);
      doc.setFontSize(11);
      doc.text('E-TICKET ITINERARY', 20, 28);
      doc.setFontSize(10);
      doc.text(`Issued: ${new Date().toLocaleDateString('en-GB')}`, 160, 18);
      
      let yPos = 45;
      
      // PNR Section
      if (bookingResult?.pnr) {
        doc.setFillColor(26, 26, 46);
        doc.rect(15, yPos - 5, 180, 22, 'F');
        doc.setFontSize(9);
        doc.setTextColor(170, 170, 170);
        doc.text('BOOKING REFERENCE', 20, yPos + 2);
        doc.setFontSize(20);
        doc.setTextColor(0, 212, 170);
        doc.text(bookingResult.pnr, 20, yPos + 14);
        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.text('CONFIRMED', 160, yPos + 8);
        doc.setTextColor(170, 170, 170);
        doc.text(bookingResult?.pnr_source === 'sabre' ? 'SABRE GDS' : 'Reference', 160, yPos + 14);
        yPos += 28;
      }
      
      // Passengers Section
      doc.setFontSize(12);
      doc.setTextColor(231, 49, 33);
      doc.text('PASSENGER DETAILS', 20, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      if (passengers && passengers.length > 0) {
        passengers.forEach((p, idx) => {
          const passengerType = p.type === 'ADULT' ? 'ADULT' : p.type === 'CHILD' ? 'CHILD' : p.type === 'INFANT' ? 'INFANT' : 'ADULT';
          const name = `${(p.title || 'MR').toUpperCase()} ${(p.first_name || '').toUpperCase()} ${(p.last_name || '').toUpperCase()}`;
          doc.text(`${idx + 1}. ${name} - ${passengerType}`, 20, yPos);
          yPos += 6;
        });
      } else {
        doc.text('1. Passenger details not available', 20, yPos);
        yPos += 6;
      }
      yPos += 5;
      
      // Outbound Flight Section
      doc.setFillColor(240, 247, 255);
      doc.rect(15, yPos - 3, 180, 45, 'F');
      
      doc.setFontSize(12);
      doc.setTextColor(231, 49, 33);
      doc.text('OUTBOUND FLIGHT', 20, yPos + 3);
      yPos += 10;
      
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text(`${flight?.from || 'N/A'}  -->  ${flight?.to || 'N/A'}`, 20, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.text(`${flight?.airline || 'Airline'} | ${flight?.airline_code || ''} ${flight?.flight_number || ''}`, 20, yPos);
      yPos += 6;
      doc.text(`Departure: ${cleanTime(flight?.departure_time)} | Arrival: ${cleanTime(flight?.arrival_time)}`, 20, yPos);
      yPos += 6;
      doc.text(`Date: ${formatDate(flight?.departure_date || flight?.departure_datetime, flight?.departure_date) || 'N/A'}`, 20, yPos);
      yPos += 6;
      const stops = flight?.stops ?? 0;
      doc.text(`Duration: ${formatDuration(flight?.duration) || 'N/A'} | ${stops === 0 ? 'DIRECT' : stops + ' STOP'}`, 20, yPos);
      yPos += 10;
      
      if (flight?.layovers && flight.layovers.length > 0) {
        doc.setTextColor(133, 100, 4);
        const layoverText = flight.layovers.map(l => `${l.airport} (${l.duration || 'N/A'})`).join(' -> ');
        doc.text(`Connection: ${layoverText}`, 20, yPos);
        yPos += 8;
      }
      
      // Return Flight Section
      if (flight?.return_departure_time) {
        yPos += 5;
        doc.setFillColor(240, 247, 255);
        doc.rect(15, yPos - 3, 180, 45, 'F');
        
        doc.setFontSize(12);
        doc.setTextColor(231, 49, 33);
        doc.text('RETURN FLIGHT', 20, yPos + 3);
        yPos += 10;
        
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text(`${flight?.to || 'N/A'}  -->  ${flight?.from || 'N/A'}`, 20, yPos);
        yPos += 8;
        
        doc.setFontSize(10);
        doc.text(`${flight?.airline || 'Airline'} | ${flight?.return_airline_code || flight?.airline_code || ''} ${flight?.return_flight_number || ''}`, 20, yPos);
        yPos += 6;
        doc.text(`Departure: ${cleanTime(flight?.return_departure_time)} | Arrival: ${cleanTime(flight?.return_arrival_time)}`, 20, yPos);
        yPos += 6;
        doc.text(`Date: ${formatDate(flight?.return_departure_date || flight?.return_departure_datetime, flight?.return_departure_date) || 'N/A'}`, 20, yPos);
        yPos += 6;
        const returnStops = flight?.return_stops ?? 0;
        doc.text(`Duration: ${formatDuration(flight?.return_duration) || 'N/A'} | ${returnStops === 0 ? 'DIRECT' : returnStops + ' STOP'}`, 20, yPos);
        yPos += 10;
        
        if (flight?.return_layovers && flight.return_layovers.length > 0) {
          doc.setTextColor(133, 100, 4);
          const returnLayoverText = flight.return_layovers.map(l => `${l.airport} (${l.duration || 'N/A'})`).join(' -> ');
          doc.text(`Connection: ${returnLayoverText}`, 20, yPos);
          yPos += 8;
        }
      }
      
      // Contact Information
      yPos += 5;
      if (contactEmail) {
        doc.setFontSize(10);
        doc.setTextColor(231, 49, 33);
        doc.text('CONTACT', 20, yPos);
        yPos += 6;
        doc.setTextColor(0, 0, 0);
        doc.text(`Email: ${contactEmail} | Phone: ${contactPhone || 'Not provided'}`, 20, yPos);
        yPos += 10;
      }
      
      // Price Section
      doc.setFillColor(26, 26, 46);
      doc.rect(15, yPos, 180, 25, 'F');
      doc.setTextColor(0, 212, 170);
      doc.setFontSize(18);
      doc.text(`TOTAL: GBP ${safePrice(priceBreakdown?.grandTotal)}`, 20, yPos + 15);
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text(`${totalPassengers || 1} Passenger(s) | All taxes included`, 120, yPos + 15);
      
      // Footer
      doc.setFontSize(9);
      doc.setTextColor(128, 128, 128);
      doc.text('Flight380 | www.flight380.co.uk | support@flight380.co.uk', 105, 275, { align: 'center' });
      doc.text('Please arrive at least 3 hours before international departures', 105, 282, { align: 'center' });
      
      doc.save(`Flight380_ETicket_${bookingResult?.pnr || (flight?.from || 'XXX') + '_' + (flight?.to || 'XXX')}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again or use Print option.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handlePrintConfirmation = () => {
    window.print();
  };

  // Render Step 1: Itinerary
  if (step === 1) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-md font-bold">1</div>
              <span className="font-semibold text-brand-600 hidden sm:inline">Itinerary</span>
            </div>
            <div className="w-8 sm:w-16 h-1 bg-gradient-to-r from-brand-500 to-slate-200 rounded-full"></div>
            <div className="flex items-center gap-2 text-slate-400">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 font-bold">2</div>
              <span className="font-medium hidden sm:inline">Passengers</span>
            </div>
            <div className="w-8 sm:w-16 h-1 bg-slate-200 rounded-full"></div>
            <div className="flex items-center gap-2 text-slate-400">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 font-bold">3</div>
              <span className="font-medium hidden sm:inline">Confirmation</span>
            </div>
          </div>
        </div>

        <div className="space-y-6" ref={itineraryRef}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gradient-to-r from-slate-50 to-brand-50 p-6 rounded-xl">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Review Your Itinerary</h1>
              <p className="text-slate-600">Please review your flight details before proceeding</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePrintItinerary} className="border-brand-500 text-brand-600 hover:bg-brand-50">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button variant="outline" onClick={handleDownloadPDF} disabled={isGeneratingPDF} className="border-brand-500 text-brand-600 hover:bg-brand-50">
                {isGeneratingPDF ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Download PDF
              </Button>
            </div>
          </div>

          <Button variant="outline" onClick={onBack} className="mb-4 hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Results
          </Button>

          {/* Multi-city: one card per sector of the BFM itinerary */}
          {isMultiCity && (
            <div className="space-y-4" data-testid="booking-multicity-sectors">
              <div className="flex items-center justify-between gap-3 bg-white rounded-xl border border-slate-200 px-4 py-3">
                <div>
                  <div className="font-bold text-slate-900">Multi-city itinerary · {flight.legs.length} sectors</div>
                  <div className="text-sm text-slate-500">{flight.route_summary || flight.legs.map((l) => `${l.from} → ${l.to}`).join(' · ')}</div>
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 font-semibold whitespace-nowrap">Sabre BFM · one fare</Badge>
              </div>
              {flight.legs.map((leg, i) => (
                <BfmFlightCard key={leg.key || i} leg={leg} itin={flight} price={Math.round(flight.price / Math.max(totalPassengers, 1))} priceCaption="per person · whole trip" badges={[{ text: `Sector ${i + 1}`, tone: 'dark' }]} testId={`booking-sector-${i}`} />
              ))}
            </div>
          )}

          {/* Outbound Flight Card */}
          {!isMultiCity && (
          <Card className="overflow-hidden border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-brand-500 to-orange-500 text-white">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <PlaneTakeoff className="h-5 w-5" />
                </div>
                Outbound Flight
                <Badge className="bg-white/20 text-white border-white/30 ml-auto">Departure</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-brand-100 to-brand-200 rounded-xl flex items-center justify-center shadow-sm">
                    <span className="text-sm sm:text-lg font-bold text-brand-700">{flight.airline_code}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{flight.airline}</div>
                    <div className="text-sm text-slate-500">Flight {flight.airline_code}</div>
                  </div>
                </div>
                <Badge className={flight.is_direct ? 'bg-emerald-100 text-emerald-700 font-semibold' : 'bg-amber-100 text-amber-700 font-semibold'}>
                  {flight.is_direct ? 'Direct' : `${flight.stops} Stop(s)`}
                </Badge>
              </div>
              
              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900">{formatTime(flight.departure_time)}</div>
                  <div className="text-lg font-medium text-brand-600">{flight.from}</div>
                  <div className="text-sm text-slate-500">{formatDate(flight.departure_date || flight.departure_datetime, flight.departure_date)}</div>
                </div>
                <div className="flex-1 px-4 sm:px-8 w-full sm:w-auto">
                  <div className="text-center text-sm text-slate-500 mb-2">{formatDuration(flight.duration)}</div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-brand-500"></div>
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-brand-500 to-orange-400 mx-2"></div>
                    <Plane className="h-5 w-5 text-brand-500 transform rotate-90" />
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-orange-400 to-brand-500 mx-2"></div>
                    <div className="w-3 h-3 rounded-full bg-brand-500"></div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900">{formatTime(flight.arrival_time)}</div>
                  <div className="text-lg font-medium text-brand-600">{flight.to}</div>
                  <div className="text-sm text-slate-500">{formatDate(flight.arrival_date || flight.departure_date, flight.arrival_date)}</div>
                </div>
              </div>
              
              {/* Layover Information - Outbound */}
              {flight.layovers && flight.layovers.length > 0 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Clock className="h-4 w-4" />
                    <span className="font-medium">Connection:</span>
                    {flight.layovers.map((layover, idx) => (
                      <span key={idx} className="text-sm">
                        {layover.airport} ({layover.duration_display || layover.duration || 'N/A'})
                        {idx < flight.layovers.length - 1 && ' → '}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Cabin Class & Baggage Info */}
              <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 bg-emerald-50 px-3 py-2 rounded-lg">
                  <Armchair className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">{searchParams?.travel_class || 'ECONOMY'}</span>
                </div>
                <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
                  <Briefcase className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">
                    {flight.baggage_allowance || '1 x 30KG Hold Luggage'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Return Flight Card */}
          {!isMultiCity && flight.return_departure_time && (
            <Card className="overflow-hidden border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <PlaneLanding className="h-5 w-5" />
                  </div>
                  Return Flight
                  <Badge className="bg-white/20 text-white border-white/30 ml-auto">Return</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-100 to-cyan-200 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-sm sm:text-lg font-bold text-blue-700">{flight.airline_code}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{flight.airline}</div>
                      <div className="text-sm text-slate-500">Flight {flight.airline_code}</div>
                    </div>
                  </div>
                  <Badge className={flight.return_is_direct ? 'bg-emerald-100 text-emerald-700 font-semibold' : 'bg-amber-100 text-amber-700 font-semibold'}>
                    {flight.return_is_direct ? 'Direct' : `${flight.return_stops} Stop(s)`}
                  </Badge>
                </div>
                
                <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-slate-900">{formatTime(flight.return_departure_time)}</div>
                    <div className="text-lg font-medium text-blue-600">{flight.to}</div>
                    <div className="text-sm text-slate-500">{formatDate(flight.return_departure_date || flight.return_departure_datetime, flight.return_departure_date)}</div>
                  </div>
                  <div className="flex-1 px-4 sm:px-8 w-full sm:w-auto">
                    <div className="text-center text-sm text-slate-500 mb-2">{formatDuration(flight.return_duration)}</div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 mx-2"></div>
                      <Plane className="h-5 w-5 text-blue-500 transform -rotate-90" />
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 mx-2"></div>
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-slate-900">{formatTime(flight.return_arrival_time)}</div>
                    <div className="text-lg font-medium text-blue-600">{flight.from}</div>
                    <div className="text-sm text-slate-500">{formatDate(flight.return_arrival_date || flight.return_arrival_datetime, flight.return_arrival_date)}</div>
                  </div>
                </div>
                
                {/* Layover Information - Return */}
                {flight.return_layovers && flight.return_layovers.length > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">Connection:</span>
                      {flight.return_layovers.map((layover, idx) => (
                        <span key={idx} className="text-sm">
                          {layover.airport} ({layover.duration_display || layover.duration || 'N/A'})
                          {idx < flight.return_layovers.length - 1 && ' → '}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Cabin Class & Baggage Info */}
                <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2 bg-emerald-50 px-3 py-2 rounded-lg">
                    <Armchair className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-700">{searchParams?.travel_class || 'ECONOMY'}</span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
                    <Briefcase className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">
                      {flight.return_baggage_allowance || '1 x 30KG Hold Luggage'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Price Breakdown */}
          <Card className="overflow-hidden border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Users className="h-5 w-5" />
                </div>
                Fare Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-2">
                {priceBreakdown.adult.count > 0 && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <div className="flex items-center gap-3">
                      <User className="h-5 w-5 text-slate-500" />
                      <div>
                        <div className="font-medium">Adult (18+)</div>
                        <div className="text-sm text-slate-500">{priceBreakdown.adult.count} × <Money gbp={priceBreakdown.adult.unitPrice.toFixed(2)} /></div>
                      </div>
                    </div>
                    <div className="text-lg font-semibold"><Money gbp={priceBreakdown.adult.total.toFixed(2)} /></div>
                  </div>
                )}
                {priceBreakdown.youth.count > 0 && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <div className="flex items-center gap-3">
                      <User className="h-5 w-5 text-blue-500" />
                      <div>
                        <div className="font-medium">Youth (12-17)</div>
                        <div className="text-sm text-slate-500">{priceBreakdown.youth.count} × <Money gbp={priceBreakdown.youth.unitPrice.toFixed(2)} /></div>
                      </div>
                    </div>
                    <div className="text-lg font-semibold"><Money gbp={priceBreakdown.youth.total.toFixed(2)} /></div>
                  </div>
                )}
                {priceBreakdown.child.count > 0 && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <div className="flex items-center gap-3">
                      <User className="h-5 w-5 text-orange-500" />
                      <div>
                        <div className="font-medium">Child (2–under 12 years)</div>
                        <div className="text-sm text-slate-500">{priceBreakdown.child.count} × <Money gbp={priceBreakdown.child.unitPrice.toFixed(2)} /></div>
                      </div>
                    </div>
                    <div className="text-lg font-semibold"><Money gbp={priceBreakdown.child.total.toFixed(2)} /></div>
                  </div>
                )}
                {priceBreakdown.infant.count > 0 && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <div className="flex items-center gap-3">
                      <User className="h-5 w-5 text-pink-500" />
                      <div>
                        <div className="font-medium">Infant (0–under 2 years)</div>
                        <div className="text-sm text-slate-500">{priceBreakdown.infant.count} × <Money gbp={priceBreakdown.infant.unitPrice.toFixed(2)} /></div>
                      </div>
                    </div>
                    <div className="text-lg font-semibold"><Money gbp={priceBreakdown.infant.total.toFixed(2)} /></div>
                  </div>
                )}
                <div className="flex items-center justify-between pt-4 bg-gradient-to-r from-brand-500 to-brand-600 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 -mb-4 sm:-mb-6 rounded-b-lg">
                  <div className="text-lg font-bold text-white">Total Fare</div>
                  <div className="text-2xl sm:text-3xl font-bold text-white"><Money gbp={priceBreakdown.grandTotal.toFixed(2)} /></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={() => setStep(2)} className="bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg shadow-lg hover:shadow-xl transition-all">
              Continue to Passenger Details
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render Step 2: Passenger Details
  if (step === 2) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md font-bold">
                <CheckCircle className="h-5 w-5" />
              </div>
              <span className="font-semibold text-emerald-600 hidden sm:inline">Itinerary</span>
            </div>
            <div className="w-8 sm:w-16 h-1 bg-gradient-to-r from-emerald-500 to-brand-500 rounded-full"></div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-md font-bold">2</div>
              <span className="font-semibold text-brand-600 hidden sm:inline">Passengers</span>
            </div>
            <div className="w-8 sm:w-16 h-1 bg-gradient-to-r from-brand-500 to-slate-200 rounded-full"></div>
            <div className="flex items-center gap-2 text-slate-400">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 font-bold">3</div>
              <span className="font-medium hidden sm:inline">Confirmation</span>
            </div>
          </div>
        </div>

        {/* Selected Itinerary Summary - Read Only */}
        <Card className="mb-6 overflow-hidden border-0 shadow-lg bg-gradient-to-br from-slate-50 to-blue-50">
          <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-4">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-lg">
                  <Plane className="h-5 w-5" />
                </div>
                <span className="text-lg font-semibold">Your Selected Itinerary</span>
              </div>
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                <CheckCircle className="h-3 w-3 mr-1" /> Confirmed Selection
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Multi-city sectors */}
            {isMultiCity && flight.legs.map((leg, i) => (
              <div key={leg.key || i} className="p-4 sm:p-5 border-b border-slate-200" data-testid={`booking-summary-sector-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <PlaneTakeoff className="h-4 w-4 text-brand-600" />
                  <span className="font-semibold text-slate-700">Sector {i + 1} · {leg.from} → {leg.to}</span>
                  <Badge variant="outline" className="ml-auto text-xs">{leg.stops === 0 ? 'Direct' : `${leg.stops} Stop${leg.stops !== 1 ? 's' : ''}`}</Badge>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-3 min-w-[140px]">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center border">
                      <img src={`https://images.kiwi.com/airlines/64/${leg.airline_code || 'XX'}.png`} alt={leg.airline_name || 'Airline'} className="w-7 h-7 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900 text-sm">{leg.airline_name || leg.airline}</div>
                      <div className="text-xs text-slate-500">{leg.flight_number}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <div className="text-center">
                      <div className="text-lg font-bold text-slate-900">{formatTime(leg.departure_time)}</div>
                      <div className="text-xs font-semibold text-slate-700">{leg.from}</div>
                      <div className="text-xs text-slate-500">{formatDate(leg.departure_date)}</div>
                    </div>
                    <div className="flex-1 flex flex-col items-center px-2">
                      <div className="text-xs text-slate-500 mb-1">{leg.duration || '—'}</div>
                      <div className="w-full h-[2px] bg-gradient-to-r from-slate-300 via-brand-400 to-slate-300 relative">
                        <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 text-brand-600 bg-blue-50 rounded-full" />
                      </div>
                      {leg.layovers?.length > 0 && <div className="text-xs text-amber-600 mt-1">via {leg.layovers.map((l) => l.airport || l.code).join(', ')}</div>}
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-slate-900">{formatTime(leg.arrival_time)}</div>
                      <div className="text-xs font-semibold text-slate-700">{leg.to}</div>
                      <div className="text-xs text-slate-500">{formatDate(leg.arrival_date || leg.departure_date)}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Outbound Flight */}
            {!isMultiCity && (
            <div className="p-4 sm:p-5 border-b border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <PlaneTakeoff className="h-4 w-4 text-brand-600" />
                <span className="font-semibold text-slate-700">Outbound Flight</span>
                <Badge variant="outline" className="ml-auto text-xs">
                  {flight.is_direct ? 'Direct' : `${flight.stops || 0} Stop${(flight.stops || 0) !== 1 ? 's' : ''}`}
                </Badge>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Airline Info */}
                <div className="flex items-center gap-3 min-w-[140px]">
                  <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center border">
                    <img 
                      src={`https://images.kiwi.com/airlines/64/${flight.airline_code || 'XX'}.png`}
                      alt={flight.airline || 'Airline'}
                      className="w-7 h-7 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 text-sm">{flight.airline || flight.airline_name || 'Airline'}</div>
                    <div className="text-xs text-slate-500">{flight.airline_code} {flight.flight_number}</div>
                  </div>
                </div>

                {/* Flight Times */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="text-center">
                    <div className="text-lg font-bold text-slate-900">{formatTime(flight.departure_time)}</div>
                    <div className="text-xs font-semibold text-slate-700">{flight.from}</div>
                    <div className="text-xs text-slate-500">{formatDate(flight.departure_date || searchParams?.departure_date)}</div>
                  </div>
                  
                  <div className="flex-1 flex flex-col items-center px-2">
                    <div className="text-xs text-slate-500 mb-1">{flight.duration || '—'}</div>
                    <div className="w-full h-[2px] bg-gradient-to-r from-slate-300 via-brand-400 to-slate-300 relative">
                      <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 text-brand-600 bg-blue-50 rounded-full" />
                    </div>
                    {flight.layovers && flight.layovers.length > 0 && (
                      <div className="text-xs text-amber-600 mt-1">
                        via {flight.layovers.map(l => l.airport || l.code).join(', ')}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-center">
                    <div className="text-lg font-bold text-slate-900">{formatTime(flight.arrival_time)}</div>
                    <div className="text-xs font-semibold text-slate-700">{flight.to}</div>
                    <div className="text-xs text-slate-500">{formatDate(flight.departure_date || searchParams?.departure_date)}</div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Return Flight */}
            {!isMultiCity && (flight.return_departure_time || flight.return_flight_number) && (
              <div className="p-4 sm:p-5 border-b border-slate-200 bg-slate-50/50">
                <div className="flex items-center gap-2 mb-3">
                  <PlaneLanding className="h-4 w-4 text-emerald-600" />
                  <span className="font-semibold text-slate-700">Return Flight</span>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {flight.return_is_direct ? 'Direct' : `${flight.return_stops || 0} Stop${(flight.return_stops || 0) !== 1 ? 's' : ''}`}
                  </Badge>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Airline Info */}
                  <div className="flex items-center gap-3 min-w-[140px]">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center border">
                      <img 
                        src={`https://images.kiwi.com/airlines/64/${flight.return_airline_code || flight.airline_code || 'XX'}.png`}
                        alt={flight.return_airline || flight.airline || 'Airline'}
                        className="w-7 h-7 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900 text-sm">{flight.return_airline || flight.return_airline_name || flight.airline || 'Airline'}</div>
                      <div className="text-xs text-slate-500">{flight.return_airline_code || flight.airline_code} {flight.return_flight_number}</div>
                    </div>
                  </div>

                  {/* Flight Times */}
                  <div className="flex items-center gap-3 flex-1">
                    <div className="text-center">
                      <div className="text-lg font-bold text-slate-900">{formatTime(flight.return_departure_time)}</div>
                      <div className="text-xs font-semibold text-slate-700">{flight.to}</div>
                      <div className="text-xs text-slate-500">{formatDate(flight.return_departure_date || searchParams?.return_date)}</div>
                    </div>
                    
                    <div className="flex-1 flex flex-col items-center px-2">
                      <div className="text-xs text-slate-500 mb-1">{flight.return_duration || '—'}</div>
                      <div className="w-full h-[2px] bg-gradient-to-r from-slate-300 via-emerald-400 to-slate-300 relative">
                        <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 text-emerald-600 bg-slate-50 rounded-full transform rotate-180" />
                      </div>
                      {flight.return_layovers && flight.return_layovers.length > 0 && (
                        <div className="text-xs text-amber-600 mt-1">
                          via {flight.return_layovers.map(l => l.airport || l.code).join(', ')}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-center">
                      <div className="text-lg font-bold text-slate-900">{formatTime(flight.return_arrival_time)}</div>
                      <div className="text-xs font-semibold text-slate-700">{flight.from}</div>
                      <div className="text-xs text-slate-500">{formatDate(flight.return_departure_date || searchParams?.return_date)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Flight Details Footer */}
            <div className="p-4 bg-gradient-to-r from-slate-100 to-blue-100">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  {/* Cabin Class */}
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <Armchair className="h-4 w-4" />
                    <span>{flight.cabin_class || searchParams?.cabin_class || 'Economy'}</span>
                  </div>
                  {/* Baggage */}
                  {(flight.baggage_included !== false) && (
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <Briefcase className="h-4 w-4" />
                      <span>{flight.baggage_count || 1}x {flight.baggage_weight || '23kg'} Baggage</span>
                    </div>
                  )}
                  {/* Passengers */}
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <Users className="h-4 w-4" />
                    <span>{totalPassengers} Passenger{totalPassengers !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                
                {/* Price */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Total Price:</span>
                  <span className="text-xl font-bold text-brand-600">
                    {flight.currency || searchParams?.currency || '£'}{(flight.combinedPrice || flight.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gradient-to-r from-slate-50 to-brand-50 p-6 rounded-xl">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Passenger Details</h1>
              <p className="text-slate-600">Enter passenger information for your booking</p>
            </div>
          </div>

          <Button variant="outline" onClick={() => setStep(1)} className="hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Itinerary
          </Button>

          {error && (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Passenger Forms - First passenger is Lead Passenger with email/phone */}
          <div className="space-y-4" ref={contactFormRef} data-testid="passenger-forms">
            {passengers.map((passenger, index) => (
              <PassengerCard key={`passenger-${index}`} index={index} total={passengers.length} type={passenger.type} lead={index === 0} testId={`passenger-card-${index}`}>
                <PassengerFields
                  form={passenger}
                  update={(k, v) => updatePassengerField(index, k, v)}
                  passengerType={passenger.type}
                  testIdPrefix={`passenger-${index}`}
                  minExpiry={flight.return_departure_date || flight.departure_date || undefined}
                />
                {index === 0 && (
                  <ContactFields email={contactEmail} phone={contactPhone} onEmail={setContactEmail} onPhone={setContactPhone} testIdPrefix="booking-contact" emailId="email" phoneId="phone" className="pt-4 border-t border-slate-100" />
                )}
              </PassengerCard>
            ))}
          </div>

          {/* Terms and Conditions */}
          <Card className="overflow-hidden border-2 border-amber-200 shadow-lg" data-testid="terms-conditions-card">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <FileText className="h-5 w-5" />
                Terms and Conditions
              </CardTitle>
              <p className="text-sm text-amber-700 font-medium mt-1">
                Important: All flight tickets purchased are non-refundable.
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-64 overflow-y-auto text-sm text-slate-700 space-y-4">
                <p className="text-slate-600">
                  All fares offered are <strong>SPECIAL FARES</strong>. By proceeding with the booking, you confirm that you have read, understood, and accepted the following conditions.
                </p>
                
                <div>
                  <h4 className="font-bold text-slate-800 mb-1">Non-Refundable Flights</h4>
                  <p>All flights are strictly non-refundable. Some tickets may be changeable only if permitted by the airline, subject to airline rules, fare conditions, availability, and applicable fees. Any permitted changes are entirely at the discretion of the airline.</p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-800 mb-1">Cancellations & No-Show</h4>
                  <p>If you cancel your booking or fail to travel on the booked flight(s), you may forfeit part or up to the full fare per passenger, depending on airline rules. All cancellation requests must be made in writing.</p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-800 mb-1">Fare Increases & Surcharges</h4>
                  <p>After payment, if the airline or operating carrier imposes any fuel surcharge, tax increase, or additional charge not included at the time of invoicing, such charges must be paid prior to ticket issuance, where permitted by law.</p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-800 mb-1">Ticket Conditions</h4>
                  <p>Once issued, tickets are non-refundable. Changes may be permitted only on selected fares, subject to airline regulations, availability, and applicable charges.</p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-800 mb-1">Passport & Visa Responsibility</h4>
                  <p>It is the client's sole responsibility to ensure compliance with all passport, visa, transit, and travel documentation requirements before travel.</p>
                </div>

                <div>
                  <h4 className="font-bold text-slate-800 mb-1">Your Financial Protection (ATOL)</h4>
                  <p>When you purchase an ATOL-protected flight or flight-inclusive holiday, you will receive an ATOL Certificate. This confirms what is financially protected and who to contact if things go wrong.</p>
                  <p className="mt-2">We, or the suppliers listed on your ATOL Certificate, will provide the services stated (or a suitable alternative). In certain insolvency situations, an alternative ATOL holder may provide the services at no additional cost to you.</p>
                  <p className="mt-2">If no alternative ATOL holder can be appointed, you may claim under the ATOL (or via your credit card issuer where applicable).</p>
                </div>
              </div>

              {/* Checkbox Acceptance */}
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500 cursor-pointer"
                    data-testid="terms-checkbox"
                  />
                  <span className="text-sm text-amber-900">
                    <strong>I confirm</strong> that I have read and accepted the Terms & Conditions and understand that all flights are <strong>non-refundable</strong> and subject to airline rules.
                  </span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Price Summary */}
          <Card className="overflow-hidden border-0 shadow-lg">
            <div className="bg-gradient-to-r from-brand-500 to-orange-500 p-4 sm:p-6">
              <div className="flex items-center justify-between text-white">
                <div>
                  <div className="text-lg font-bold">Total Fare</div>
                  <div className="text-sm text-white/80">{totalPassengers} passenger(s)</div>
                </div>
                <div className="text-2xl sm:text-3xl font-bold"><Money gbp={priceBreakdown.grandTotal.toFixed(2)} /></div>
              </div>
              {payingDeposit && (
                <div className="mt-3 pt-3 border-t border-white/30 flex items-center justify-between text-white" data-testid="deposit-summary">
                  <div className="text-sm">Due today (deposit)<div className="text-xs text-white/80">Balance <Money gbp={Number(depositQuote.balance_due).toFixed(2)} /> due by {new Date(`${depositQuote.balance_due_date}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div></div>
                  <div className="text-xl sm:text-2xl font-bold"><Money gbp={Number(depositQuote.deposit).toFixed(2)} /></div>
                </div>
              )}
            </div>
          </Card>

          <FlightPaymentPlan quote={depositQuote} total={priceBreakdown.grandTotal} plan={paymentPlan} onChange={setPaymentPlan} />

          <PassportValidityNotice checked={passportOk} onChange={setPassportOk} blocked={passportBlocked} testIdPrefix="flight-passport" />
          <NonRefundableNotice accepted={noticeAccepted} onChange={(v) => { setNoticeAccepted(v); setNoticeAcceptedAt(v ? new Date().toISOString() : null); }} />

          <FinalBookingConfirmation accepted={finalAccepted} onChange={setFinalAccepted} />

          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <Button variant="outline" onClick={() => setStep(1)} className="hover:bg-slate-100">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button 
              onClick={handleSubmitBooking}
              disabled={isSubmitting || !termsAccepted || !finalAccepted || !noticeAccepted}
              className={`px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg shadow-lg transition-all ${
                termsAccepted && finalAccepted && noticeAccepted
                  ? 'bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 hover:shadow-xl' 
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
              data-testid="confirm-booking-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Redirecting to secure payment…
                </>
              ) : (
                <>
                  {!(termsAccepted && finalAccepted && noticeAccepted) ? <AlertCircle className="h-5 w-5 mr-2" /> : <CreditCard className="h-5 w-5 mr-2" />}
                  {payingDeposit ? <>Pay <Money gbp={Number(amountDueNow).toFixed(2)} /> Deposit &amp; Hold Seats</> : <>Pay <Money gbp={priceBreakdown.grandTotal.toFixed(2)} /> &amp; Confirm Booking</>}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>
          </div>
          
          {!(termsAccepted && finalAccepted && noticeAccepted) && (
            <p className="text-center text-amber-600 text-sm" data-testid="terms-warning">
              Please accept the Terms & Conditions, the Non-Refundable Fare Notice and the Final Booking Confirmation to proceed with payment
            </p>
          )}
        </div>
      </div>
    );
  }

  // Render Step 3: Confirmation
  if (step === 3) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md">
                <CheckCircle className="h-5 w-5" />
              </div>
              <span className="font-semibold text-emerald-600 hidden sm:inline">Itinerary</span>
            </div>
            <div className="w-8 sm:w-16 h-1 bg-emerald-500 rounded-full"></div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md">
                <CheckCircle className="h-5 w-5" />
              </div>
              <span className="font-semibold text-emerald-600 hidden sm:inline">Passengers</span>
            </div>
            <div className="w-8 sm:w-16 h-1 bg-emerald-500 rounded-full"></div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md font-bold">3</div>
              <span className="font-semibold text-emerald-600 hidden sm:inline">Confirmation</span>
            </div>
          </div>
        </div>

        <div className="space-y-6" ref={confirmationRef}>
          {/* Success Header with PNR - Combined for better visibility */}
          <Card className="overflow-hidden border-0 shadow-2xl">
            {/* Success Banner */}
            <div className="text-center py-8 bg-gradient-to-br from-emerald-500 via-emerald-600 to-cyan-600 text-white">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle className="h-12 w-12 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="booking-step3-title">Seats Reserved — Complete Payment</h2>
              <p className="text-emerald-100">Your booking reference is ready. Pay now with card to issue your tickets.</p>
            </div>
            
            {/* PNR Display - Prominent Section */}
            <div 
              ref={pnrSectionRef}
              className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 p-6 sm:p-10 text-center"
              data-testid="pnr-section"
            >
              <div className="text-sm text-slate-400 mb-3 font-medium uppercase tracking-wider">Your Booking Reference (PNR)</div>
              <div className="flex items-center justify-center gap-4 mb-4">
                <div 
                  className="text-5xl sm:text-7xl font-black text-white tracking-[0.2em] bg-gradient-to-r from-brand-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent select-all cursor-text"
                  data-testid="pnr-display"
                  onClick={() => {
                    // Select PNR text on click for easy manual copy
                    const selection = window.getSelection();
                    const range = document.createRange();
                    range.selectNodeContents(document.querySelector('[data-testid="pnr-display"]'));
                    selection.removeAllRanges();
                    selection.addRange(range);
                  }}
                >
                  {bookingResult?.pnr}
                </div>
              </div>
              {/* Show PNR source indicator */}
              {bookingResult?.pnr_source === 'sabre' && (
                <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-full mb-4">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Real Airline Confirmation - Verify in your GDS</span>
                </div>
              )}
              {bookingResult?.pnr_source === 'mock' && bookingResult?.flight_source !== 'sabre' && (
                <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 px-4 py-2 rounded-full mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Local Reference - Demo Mode</span>
                </div>
              )}
              <div className="flex items-center justify-center gap-3">
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => copyToClipboard(bookingResult?.pnr)}
                  className={`border-white/30 text-white hover:bg-white/20 transition-all duration-300 ${
                    pnrCopied 
                      ? 'bg-emerald-500/30 border-emerald-400/50' 
                      : 'bg-white/10'
                  }`}
                  data-testid="copy-pnr-btn"
                >
                  {pnrCopied ? (
                    <>
                      <Check className="h-4 w-4 mr-2 text-emerald-400" />
                      <span className="text-emerald-300">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy PNR
                    </>
                  )}
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-4">Please save this reference number for your records</p>
              <p className="text-xs text-emerald-400 mt-2">Confirmation email with e-ticket sent to {contactEmail}</p>
            </div>
          </Card>

          {/* Pay now with card (Revolut hosted checkout) */}
          <Card className="overflow-hidden border-2 border-brand-500 shadow-xl" data-testid="flight-payment-card">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-brand-600 mb-1">Amount due</div>
                  <div className="text-4xl font-black text-slate-900" data-testid="flight-payment-amount"><Money gbp={priceBreakdown.grandTotal.toFixed(2)} /></div>
                  <p className="text-sm text-slate-600 mt-2">
                    {totalPassengers} passenger{totalPassengers !== 1 ? 's' : ''} · all taxes &amp; fees included · secure card payment via Revolut
                  </p>
                </div>
                <div className="flex flex-col gap-2 md:min-w-[260px]">
                  <Button
                    onClick={handlePayNow}
                    disabled={isPaying}
                    className="h-14 text-lg font-bold bg-brand-600 hover:bg-brand-700 text-white shadow-lg"
                    data-testid="pay-now-card-btn"
                  >
                    {isPaying ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CreditCard className="h-5 w-5 mr-2" />}
                    {isPaying ? 'Redirecting…' : 'Pay now with card'}
                  </Button>
                  <div className="text-xs text-slate-500 text-center">Visa · Mastercard · Apple Pay · Google Pay</div>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-4 border-t border-slate-100 pt-3">
                Prefer to pay later? Your reference <span className="font-semibold text-slate-700">{bookingResult?.pnr}</span> is held — call 01908 220000 or 01582 808050 before the payment deadline.
              </p>
            </CardContent>
          </Card>
          <Card className="overflow-hidden border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <FileText className="h-5 w-5" />
                </div>
                Booking Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                  <div className="text-sm text-blue-600 font-medium mb-1">Route</div>
                  <div className="text-lg font-bold text-blue-900">{isMultiCity ? flight.route_summary : `${flight.from} → ${flight.to}`}</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
                  <div className="text-sm text-purple-600 font-medium mb-1">Departure</div>
                  <div className="text-lg font-bold text-purple-900">{formatDate(flight.departure_date || flight.departure_datetime, flight.departure_date)}</div>
                </div>
                {flight.return_departure_time && (
                  <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-4 rounded-xl">
                    <div className="text-sm text-cyan-600 font-medium mb-1">Return</div>
                    <div className="text-lg font-bold text-cyan-900">{formatDate(flight.return_departure_date || flight.return_departure_datetime, flight.return_departure_date)}</div>
                  </div>
                )}
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-xl">
                  <div className="text-sm text-amber-600 font-medium mb-1">Passengers</div>
                  <div className="text-lg font-bold text-amber-900">{totalPassengers}</div>
                </div>
                <div className="bg-gradient-to-br from-brand-50 to-brand-100 p-4 rounded-xl">
                  <div className="text-sm text-brand-600 font-medium mb-1">Total Price</div>
                  <div className="text-2xl font-bold text-brand-700"><Money gbp={priceBreakdown.grandTotal.toFixed(2)} /></div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-xl">
                  <div className="text-sm text-amber-600 font-medium mb-1">Status</div>
                  <Badge className="bg-amber-500 text-white text-sm font-bold px-3 py-1" data-testid="booking-payment-status">PAYMENT PENDING</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email Confirmations */}
          {bookingResult?.emails && bookingResult.emails.length > 0 && (
            <Card className="overflow-hidden border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Mail className="h-5 w-5" />
                  </div>
                  Confirmation Emails
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <Tabs defaultValue={bookingResult.emails[0]?.to}>
                  <TabsList className="w-full flex-wrap h-auto gap-1 bg-slate-100 p-1">
                    {bookingResult.emails.map((email, idx) => (
                      <TabsTrigger key={idx} value={email.to} className="text-xs sm:text-sm data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                        {email.to.includes('customer') || email.to === contactEmail ? 'Customer' : 'Agent'}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {bookingResult.emails.map((email, idx) => (
                    <TabsContent key={idx} value={email.to}>
                      <div className="mt-4 bg-gradient-to-br from-slate-50 to-purple-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="h-4 w-4" />
                          <span>To: {email.to}</span>
                        </div>
                        <div className="font-semibold text-slate-900 mt-1">{email.subject}</div>
                        <Separator className="my-4" />
                        <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono bg-white p-4 rounded-lg border max-h-64 sm:max-h-96 overflow-y-auto shadow-inner">
                          {email.body}
                        </pre>
                        <div className="mt-4 flex items-center gap-2 text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg w-fit">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">Email sent successfully</span>
                        </div>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Button variant="outline" onClick={handlePrintItinerary} className="px-8 py-6 text-base border-2 hover:bg-slate-50">
              <Printer className="h-5 w-5 mr-2" />
              Print Itinerary
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} disabled={isGeneratingPDF} className="px-8 py-6 text-base border-2 border-brand-500 text-brand-600 hover:bg-brand-50">
              {isGeneratingPDF ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Download className="h-5 w-5 mr-2" />}
              Download PDF
            </Button>
            <Button onClick={onComplete} className="bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 px-8 py-6 text-base shadow-lg hover:shadow-xl transition-all">
              Search New Flight
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
